import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient } from '../api/client'
import { TopBar } from '../components/TopBar'
import { Sidebar } from '../components/Sidebar'
import { useState } from 'react'
import { getUser } from '../auth'
import type { Post } from '../types'

const DOC_TYPES = ['beitrag', 'docu', 'angebot', 'story', 'review', 'survey', 'audio_guide', 'agent']

export function Dashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newDocType, setNewDocType] = useState('beitrag')
  const [newTags, setNewTags] = useState('')
  const [newLang, setNewLang] = useState('de')
  const [creating, setCreating] = useState(false)

  const handleDelete = async (post: Post, e: React.MouseEvent) => {
    e.stopPropagation()
    const ok = window.confirm(
      `"${post.title}" wirklich löschen?\n\nDieser Vorgang ist nicht umkehrbar.`,
    )
    if (!ok) return
    try {
      await apiClient.deletePost(post.id)
      queryClient.invalidateQueries({ queryKey: ['posts'] })
    } catch (err) {
      alert(`Löschen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  const handleCreate = async () => {
    if (!newTitle.trim()) return
    setCreating(true)
    try {
      const tags = newTags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
      // Pick a content tenant from the JWT roles. Server auto-fills if
      // omitted, but sending it explicitly avoids a round-trip and makes
      // the chosen tenant obvious to the user. Fallback "arkturian" for
      // the rare superadmin-with-no-content-roles case.
      const user = getUser()
      const contentRole = user?.roles.find(r => r.sys === 'content' && r.tid)
      const partnerId = contentRole?.tid || 'arkturian'
      const post = await apiClient.createPost({
        title: newTitle.trim(),
        doc_type: newDocType,
        partner_id: partnerId,
        tags: tags.length > 0 ? tags : undefined,
        language: newLang,
        status: 'draft',
        content_type: 'md',
        author_id: user?.id || 'user',
        author_name: user ? `${user.first_name} ${user.last_name}` : 'User',
        content: '',
      })
      queryClient.invalidateQueries({ queryKey: ['posts'] })
      setShowNew(false)
      setNewTitle('')
      setNewTags('')
      navigate(`/post/${post.id}`)
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setCreating(false)
    }
  }

  const collectionSlug = searchParams.get('collection')
  const docTypeFilter = searchParams.get('doc_type')
  const tagFilter = searchParams.get('tag')
  const searchQuery = (searchParams.get('q') || '').trim()

  // Fetch posts
  const { data, isLoading } = useQuery({
    queryKey: ['posts', docTypeFilter, searchQuery],
    queryFn: () => apiClient.listPosts({
      limit: 100,
      doc_type: docTypeFilter || undefined,
      search: searchQuery || undefined,
    }),
  })

  // Fetch collection posts if filtered
  const { data: collectionData } = useQuery({
    queryKey: ['collection', collectionSlug],
    queryFn: () => apiClient.getCollection(collectionSlug!),
    enabled: !!collectionSlug,
  })

  let posts: Post[] = data?.posts || []

  // Filter by collection
  if (collectionSlug && collectionData?.posts) {
    const collectionPostIds = new Set(collectionData.posts.map((p: Post) => p.id))
    posts = posts.filter(p => collectionPostIds.has(p.id))
  }

  // Filter by tag
  if (tagFilter) {
    posts = posts.filter(p => p.tags?.some(t => t.toLowerCase() === tagFilter.toLowerCase()))
  }

  // Deduplicate language variants — show one post per language_group
  // Prefer the original (language === source_language), keep in position
  const groupWinner = new Map<string, Post>()
  for (const p of posts) {
    const group = p.language_group
    if (!group) continue
    const existing = groupWinner.get(group)
    if (!existing) {
      groupWinner.set(group, p)
    } else {
      const pIsOriginal = p.source_language && p.language === p.source_language
      const existingIsOriginal = existing.source_language && existing.language === existing.source_language
      if (pIsOriginal && !existingIsOriginal) {
        groupWinner.set(group, p)
      }
    }
  }
  const hiddenIds = new Set<number>()
  for (const p of posts) {
    const group = p.language_group
    if (group && groupWinner.get(group)?.id !== p.id) {
      hiddenIds.add(p.id)
    }
  }
  posts = posts.filter(p => !hiddenIds.has(p.id))

  // Title
  let title = 'All Posts'
  if (collectionSlug) title = collectionData?.name || collectionSlug
  if (docTypeFilter) title = docTypeFilter.charAt(0).toUpperCase() + docTypeFilter.slice(1)
  if (tagFilter) title = `#${tagFilter}`

  return (
    <>
      <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} breadcrumb={title} actions={
        <button className="topbar-btn topbar-btn--accent" onClick={() => setShowNew(!showNew)}>
          <i className="fas fa-plus" style={{ marginRight: 4 }} /> New
        </button>
      } />
      <div className="app">
        <Sidebar open={sidebarOpen} />
        <div className="workspace">
          <div className="workspace-inner" style={{ maxWidth: 1000 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 700 }}>
                {title}
              </h1>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {posts.length} posts
              </span>
              {(collectionSlug || docTypeFilter || tagFilter) && (
                <button
                  onClick={() => navigate('/')}
                  style={{
                    marginLeft: 'auto', fontSize: 11, padding: '3px 10px',
                    border: '1px solid var(--border)', background: 'var(--white)',
                    cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                >
                  <i className="fas fa-times" style={{ marginRight: 4 }} /> Clear filter
                </button>
              )}
            </div>

            {showNew && (
              <div className="new-post-form">
                <input
                  className="new-post-title"
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Title..."
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreate() }}
                />
                <div className="new-post-row">
                  <div className="new-post-field">
                    <span className="new-post-label">Type</span>
                    <select value={newDocType} onChange={(e) => setNewDocType(e.target.value)}>
                      {DOC_TYPES.map(dt => <option key={dt} value={dt}>{dt}</option>)}
                    </select>
                  </div>
                  <div className="new-post-field">
                    <span className="new-post-label">Language</span>
                    <select value={newLang} onChange={(e) => setNewLang(e.target.value)}>
                      <option value="de">DE</option>
                      <option value="en">EN</option>
                      <option value="sl">SL</option>
                      <option value="it">IT</option>
                      <option value="es">ES</option>
                    </select>
                  </div>
                  <div className="new-post-field" style={{ flex: 1 }}>
                    <span className="new-post-label">Tags</span>
                    <input
                      type="text"
                      value={newTags}
                      onChange={(e) => setNewTags(e.target.value)}
                      placeholder="tag1, tag2, tag3"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreate() }}
                    />
                  </div>
                  <button
                    className="topbar-btn topbar-btn--accent"
                    onClick={handleCreate}
                    disabled={creating || !newTitle.trim()}
                  >
                    {creating ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-check" style={{ marginRight: 4 }} /> Create</>}
                  </button>
                  <button className="topbar-btn" onClick={() => setShowNew(false)}>
                    <i className="fas fa-times" />
                  </button>
                </div>
              </div>
            )}

            {isLoading && <p style={{ color: 'var(--text-muted)' }}>Loading...</p>}

            <div style={{ display: 'grid', gap: 1 }}>
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="post-list-item"
                  onClick={() => navigate(`/post/${post.id}`)}
                >
                  <span className="post-list-id">#{post.id}</span>
                  <div className="post-list-info">
                    <div className="post-list-title">
                      {/* P2.9: live-collab indicator. CRDT-enabled posts
                          mutate in realtime via WebSocket; plain posts
                          are static-versioned. Visual cue helps users +
                          bots avoid tool-confusion (e.g. calling
                          doc_apply_text on a static post). */}
                      {post.collab_enabled && (
                        <span
                          title="Live-Collab — CRDT-Doc"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 2,
                            marginRight: 6,
                            padding: '1px 6px',
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#1e3a8a',
                            background: '#dbeafe',
                            border: '1px solid #93c5fd',
                            borderRadius: 4,
                            verticalAlign: 'middle',
                          }}
                        >
                          ● live
                        </span>
                      )}
                      {post.title}
                    </div>
                    <div className="post-list-meta">
                      {post.author_name} · {post.doc_type || 'post'} · {new Date(post.created_at).toLocaleDateString('de-AT')}
                      {post.tags && post.tags.length > 0 && (
                        <span className="post-list-tags">
                          {post.tags.slice(0, 3).map(t => (
                            <span key={t} className="post-list-tag">#{t}</span>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`post-list-status ${post.status === 'published' ? 'published' : ''}`}>
                    {post.status}
                  </span>
                  <button
                    className="post-list-delete"
                    title="Löschen"
                    aria-label={`Beitrag #${post.id} löschen`}
                    onClick={(e) => handleDelete(post, e)}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              ))}
            </div>

            {!isLoading && posts.length === 0 && (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
                <i className="fas fa-folder-open" style={{ fontSize: 32, marginBottom: 12, display: 'block', opacity: 0.3 }} />
                No posts found.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
